import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { useEntryState } from "@/lib/entryState";
import Segmented from "@/components/Segmented";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Briefcase, Archive, RotateCcw, AlertTriangle, Upload, ChevronsUpDown, ChevronsDownUp, ArrowUp, ArrowDown, ChevronRight, ChevronDown } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { shortName } from "@/lib/people";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FrsDateCell from "../components/FrsDateCell";
import { nextUpcoming } from "../lib/mixedDate";
import { fmtDMY, dateTone, dateTitle, weeksOut, latestOf } from "../lib/raFormat";
import RaScopeRows from "../components/RaJobTree";
import { useScheduleSave } from "../components/ScopeLinks";
import { useAuth } from "@/lib/AuthContext";
import { MobRequestDialog } from "../components/RaScopeRequests";
import { DocChecks, rollupChecks, docColour } from "../components/RaDocChecks";
import { MaterialChecks, RiskBadge } from "../components/RaMaterialChecks";
import NotesThread from "@/components/NotesThread";
import HeaderFilterMenu from "@/estimating/components/HeaderFilterMenu";

const WINDOWS = { all: Infinity, w4: 28, w8: 56 };
const OPS_STATUS = {
  estimate: ["Estimate", "text-gray-700 bg-gray-100 border-gray-200"],
  ready: ["Ready", "text-sky-800 bg-sky-50 border-sky-200"],
  open: ["Open", "text-emerald-800 bg-emerald-50 border-emerald-200"],
  on_hold: ["On Hold", "text-amber-800 bg-amber-50 border-amber-200"],
  completed: ["Completed", "text-gray-700 bg-gray-100 border-gray-300"],
  closed: ["Closed", "text-gray-600 bg-gray-100 border-gray-300"],
  cancelled: ["Cancelled", "text-red-700 bg-red-50 border-red-200"],
};
export const OpsStatusPill = ({ value, testId }) => {
  const [label, cls] = OPS_STATUS[value] || ["Not in Operations", "text-gray-400 bg-transparent border-dashed border-gray-300"];
  return (
    <span data-testid={testId} data-status={value || ""} title="Status comes from the Operations tab"
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>{label}</span>
  );
};
const DOT_RANK = { red: 0, yellow: 1, green: 2, grey: 3 };
const worstDot = (colours) => {
  const live = colours.filter((c) => c in DOT_RANK && c !== "grey");
  return live.length ? live.sort((a, b) => DOT_RANK[a] - DOT_RANK[b])[0] : "grey";
};
const EMPTY_FORM = {
  project_name: "", job_number: "", address: "", city: "", state: "", zip_code: "",
  client_id: "", foreman_id: "", pm_id: "",
  gc_contact_name: "", gc_contact_phone: "", gc_contact_email: "",
};

function nextJobNumber(projects) {
  const nums = (projects || [])
    .map((p) => parseInt(p.job_number, 10))
    .filter((n) => !isNaN(n) && n >= 70000 && n < 80000);
  return nums.length > 0 ? String(Math.max(...nums) + 1) : "70001";
}

const NO_RELATIONS = { groups: [], breaks: [], deps: [], done: [] };

/** The job's ABS start — the one place the whole schedule moves from. Through the same engine as every
 *  level: cascade to everything inheriting, locked children asked about. A date set here shows bold;
 *  with none set it shows the next scope start (plain). No padlock: setting a date IS the lock. */
function ProjectStartCell({ project, scopeRows, actor, onChanged }) {
  const { save, dialogs } = useScheduleSave({ projectId: project.id, actor, view: "ra", rel: NO_RELATIONS, onChanged });
  const rollup = nextUpcoming((scopeRows || []).filter((r) => !r.all_complete).map((r) => r.next_start?.date));
  const locked = !!project.start_date;
  return (
    <>
      <FrsDateCell value={locked ? project.start_date : (rollup.date || "")} locked={locked}
                   past={!locked && rollup.past}
                   onCommit={(v) => save({ level: "project", scope_type: "" }, "start", v)}
                   hint={locked ? "Job start — every scope, building, floor, area and room that is not set on its own follows it"
                     : rollup.date ? "Next scope start — pick a date to set the whole job" : "Set the job start — every scope inherits it"}
                   unlockLabel="Back to the scopes' own dates"
                   testId={`project-start-${project.id}`} plain />
      {dialogs}
    </>
  );
}

/** Completion is calculated, never typed: start + remaining hours ÷ (crew × hours/day × 0.8), rounded up
 *  to working days — the job finishes when its last scope does. */
function ProjectCompletionCell({ project, scopeRows }) {
  const live = (scopeRows || []).filter((r) => !r.all_complete);
  const end = project.abs_completion_date || latestOf(live.map((r) => r.end_abs));
  if (!end) return <span className="text-[11px] text-gray-400" data-testid={`project-completion-${project.id}`}>—</span>;
  return (
    <span data-testid={`project-completion-${project.id}`}
          title={`${dateTitle(end)} · calculated from the start plus the remaining hours at the crew size (last scope to finish)`}
          className={`text-[11px] tabular-nums ${dateTone(end) || "text-gray-700"}`}>{fmtDMY(end)}</span>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [, setArchivedCount] = useState(0);
  const [view, setView] = useUrlState("view", "active"); // "active" | "archived"
  const [window_, setWindow] = useUrlState("win", "all"); // "all" | "w4" | "w8" — starts within 4 / 8 weeks
  const [foremanF, setForemanF] = useUrlState("foreman", "");   // "Name|Name" — multi, blank = everyone
  const [pmF, setPmF] = useUrlState("pm", "");
  const [groupBy, setGroupBy] = useUrlState("group", "");   // "" | "foreman" | "pm" — rows grouped by person
  const [query, setQuery] = useEntryState("q", "");
  // RA-SORT — press any column to sort; pressing it again flips the direction. No arrows: the
  // order of the rows is the feedback. Default: by job number, blanks last.
  const [sort, setSort] = useEntryState("sort", { key: "job", dir: "asc" });
  const pressColumn = (key) =>
    setSort((c) => (c && c.key === key ? { key, dir: c.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const [installers, setInstallers] = useState([]);
  const [pms, setPms] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Confirmation dialog state for archive/unarchive (yes/no prompt).
  const [confirm, setConfirm] = useState(null); // { action, project }
  const [importing, setImporting] = useState(false);
  const [scopeSummary, setScopeSummary] = useState({});
  const [scopeDates, setScopeDates] = useState({});      // project_id -> [{scope, next_start, next_abs, next_gc, rooms, complete_rooms}]
  const [scopesOpen, setScopesOpen] = useEntryState("scopesOpen", {});      // project_id -> true expands that job's scope rows (collapsed by default)
  const [treeOpen, setTreeOpen] = useEntryState("treeOpen", {});    // "pid|scope" / "pid|scope|node" -> true (RA tree)
  const [mobTarget, setMobTarget] = useState(null);      // { project, sc } — "Update lineout?"
  const [notes, setNotes] = useState({});                // project_id -> [notes newest first]
  const { user } = useAuth();
  const actor = user?.email || user?.name || "";
  // Project date editor: { project, start_date, abs_completion_date, gc_completion_date, confirming }
  const frsInputRef = useRef(null);
  const navigate = useNavigate();

  const importFromFrs = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/projects/from-frs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const scopes = (data.applied_sheets || []).map((s) => s.scope_code).join(", ");
      toast.success(
        `Created ${data.project.project_name}` +
        (scopes ? ` — ${data.total_items} items across: ${scopes}` : " (no scope data found in the FRS)")
      );
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "FRS import failed");
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pj, archCount, ins, pmsR, cl, scp, sd, nt] = await Promise.all([
        api.get("/projects", { params: { archived: view === "archived" } }),
        api.get("/projects", { params: { archived: true } }),
        api.get("/installers"),
        api.get("/pms"),
        api.get("/clients"),
        api.get("/projects-scope-summary"),
        api.get("/projects-scope-dates", { params: { archived: view === "archived" } }).catch(() => ({ data: {} })),
        api.get("/projects-notes", { params: { archived: view === "archived" } }).catch(() => ({ data: {} })),
      ]);
      setScopeSummary(scp.data || {});
      setScopeDates(sd.data || {});
      setNotes(nt.data || {});
      // jobs read top to bottom in alphabetical order
      setProjects((pj.data || []).slice().sort((a, b) =>
        String(a.project_name || "").localeCompare(String(b.project_name || ""), undefined, { sensitivity: "base" })));
      setArchivedCount((archCount.data || []).length);
      setInstallers((ins.data || []).filter((i) => i.role === "Foreman" || i.is_lead || i.role === "Lead"));
      // PM options come from Settings → Users. `active` there means "can sign in", which
      // has nothing to do with whether they can be a project's PM, so we show them all.
      setPms(pmsR.data || []);
      setClients((cl.data || []).filter((c) => c.active));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [view]);
  // requests / notes change often — refresh those two reads without the table's loading flash
  const refreshLight = async () => {
    const [sd, nt] = await Promise.all([
      api.get("/projects-scope-dates", { params: { archived: view === "archived" } }).catch(() => ({ data: {} })),
      api.get("/projects-notes", { params: { archived: view === "archived" } }).catch(() => ({ data: {} })),
    ]);
    setScopeDates(sd.data || {});
    setNotes(nt.data || {});
  };
  const addNote = async (p, text) => {
    try {
      await api.post(`/projects/${p.id}/notes`, { text });
      await refreshLight();
      return true;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post the note"); return false; }
  };
  const openRequests = (p) => liveScopes(p).reduce((n, sc) => n + Object.keys(sc.requests || {}).length, 0);

  // The job row leaves "Next area" to its scopes, so the column sorts on the first scope that
  // still has somewhere to go.
  const nextAreaText = (p) => {
    for (const sc of (scopeDates[p.id] || []).filter((x) => !x.all_complete)) {
      const w = sc.next_start?.where;
      if (w && w.length) return Array.isArray(w) ? w.join(" ") : String(w);
    }
    return "";
  };
  const LAST = "\uffff"; // blanks sort to the bottom, whichever way the column is pressed
  const liveScopes = (p) => (scopeDates[p.id] || []).filter((x) => !x.all_complete);
  const projectDots = (p) => ({
    material: worstDot(liveScopes(p).map((x) => x.material)),
    doc: worstDot(liveScopes(p).map((x) => x.doc)),
  });
  const foremanName = (p) => installers.find((i) => i.id === p.foreman_id)?.name || "";
  const jobStart = (p) => p.start_date || nextUpcoming(liveScopes(p).map((r) => r.next_start?.date)).date || "";
  const riskReasons = (p) => liveScopes(p).filter((x) => x.at_risk).flatMap((x) => (x.risk_reasons || []).map((r) => `${x.scope}: ${r}`));
  const inWindow = (p) => {
    if (window_ === "risk") return riskReasons(p).length > 0;
    const days = WINDOWS[window_] ?? Infinity;
    if (days === Infinity) return true;
    const d = jobStart(p);
    if (!d) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((new Date(`${d}T00:00:00`) - today) / 86400000) <= days;
  };
  const sortValue = (p, key) => {
    switch (key) {
      case "job": return p.job_number ? String(p.job_number).padStart(8, "0") : LAST;
      case "project": return (p.project_name || "").toLowerCase() || LAST;
      case "foreman": return foremanName(p).toLowerCase() || LAST;
      case "pm": return (p.pm_name || "").toLowerCase() || LAST;
      case "area": return nextAreaText(p).toLowerCase() || LAST;
      case "start": return jobStart(p) || LAST;
      case "weeks": return jobStart(p) || LAST;
      case "completion": return p.abs_completion_date || latestOf(liveScopes(p).map((r) => r.end_abs)) || LAST;
      case "material": return String(DOT_RANK[projectDots(p).material]);
      case "doc": return String(DOT_RANK[docColour(rollupChecks(liveScopes(p)))]);
      case "requests": return openRequests(p) ? String(99 - openRequests(p)).padStart(2, "0") : LAST;
      case "notes": return (notes[p.id] || []).length ? String(999 - (notes[p.id] || []).length).padStart(3, "0") : LAST;
      case "status": return p.is_archived ? "archived" : (p.ops_status || LAST);
      default: return LAST;
    }
  };
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = (p) => !q || [p.project_name, p.job_number, p.address, p.city, p.client_name, p.pm_name, foremanName(p)]
      .some((v) => String(v || "").toLowerCase().includes(q))
      || (scopeDates[p.id] || []).some((sc) => String(sc.scope || "").toLowerCase().includes(q));
    const fPick = foremanF ? new Set(foremanF.split("|")) : null;
    const pPick = pmF ? new Set(pmF.split("|")) : null;
    const byPeople = (p) => (!fPick || fPick.has(foremanName(p) || "—")) && (!pPick || pPick.has(p.pm_name || "—"));
    const list = projects.filter(hit).filter(inWindow).filter(byPeople);
    if (!sort) return list;
    const sorted = list.slice().sort((a, b) =>
      String(sortValue(a, sort.key)).localeCompare(String(sortValue(b, sort.key)),
        undefined, { numeric: true, sensitivity: "base" }) || String(a.project_name || "").localeCompare(String(b.project_name || "")));
    return sort.dir === "desc" ? sorted.reverse() : sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, sort, scopeDates, query, installers, window_, notes, foremanF, pmF]);
  // Grouped by Foreman / PM: a header row per person (Unassigned last), the sort kept inside each group.
  const items = useMemo(() => {
    if (!groupBy) return rows.map((p) => ({ p }));
    const nameOf = (p) => (groupBy === "foreman" ? foremanName(p) : p.pm_name) || "";
    const groups = new Map();
    for (const p of rows) { const n = nameOf(p); if (!groups.has(n)) groups.set(n, []); groups.get(n).push(p); }
    return [...groups.keys()].sort((a, b) => (!a) - (!b) || a.localeCompare(b))
      .flatMap((n) => [{ hdr: n, count: groups.get(n).length }, ...groups.get(n).map((p) => ({ p }))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groupBy, installers]);
  const groupHeader = (x) => (
    <tr key={`grp-${x.hdr || "none"}`} className="bg-slate-100/80 dark:bg-slate-800/60 border-t border-border" data-testid={`ra-group-${x.hdr || "unassigned"}`}>
      <td colSpan={14} className="px-3 py-1.5 text-xs font-semibold text-foreground">
        {groupBy === "foreman" ? "Foreman" : "PM"} · {x.hdr || "Unassigned"}
        <span className="ml-2 font-normal text-muted-foreground">{x.count} job{x.count === 1 ? "" : "s"}</span>
      </td>
    </tr>
  );
  // Filter options = the people actually on the listed jobs ("—" = unassigned)
  const peopleOptions = (pick) => {
    const names = [...new Set(projects.map((p) => pick(p) || "—"))].sort((a, b) => (a === "—") - (b === "—") || a.localeCompare(b));
    return names.map((n) => ({ value: n, label: n === "—" ? "Unassigned" : n }));
  };

  const submit = async () => {
    if (!form.project_name) {
      toast.error("Job name is required");
      return;
    }
    try {
      await api.post("/projects", form);
      toast.success("Job created");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      toast.error("Failed to create job");
    }
  };

  const doArchive = async (p) => {
    try {
      await api.post(`/projects/${p.id}/archive`, { reason: "manual" });
      toast.success(`Archived ${p.project_name}`);
      setConfirm(null);
      load();
    } catch { toast.error("Archive failed"); }
  };
  const doActivate = async (p) => {
    try {
      await api.post(`/projects/${p.id}/activate`);
      toast.success(`Re-activated ${p.project_name}`);
      setConfirm(null);
      load();
    } catch { toast.error("Activate failed"); }
  };

  const allOpen = rows.some((p) => liveScopes(p).length) && rows.filter((p) => liveScopes(p).length).every((p) => scopesOpen[p.id] === true);

  return (
    <div>
      <div className="flex items-center mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => setScopesOpen(allOpen ? {} : Object.fromEntries(rows.filter((p) => liveScopes(p).length).map((p) => [p.id, true])))}
                  data-testid="ra-expand-all" disabled={loading} title={allOpen ? "Collapse every job's scopes" : "Expand every job's scopes"}
                  className="grid h-7 w-7 place-items-center rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
            {allOpen ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
          </button>
          <SearchBar value={query} onChange={setQuery} placeholder="Search..." testId="ra-search-input" />
          <Segmented
            options={[{ key: "active", label: "Active", testId: "projects-view-active" },
                      { key: "archived", label: "Archived", testId: "projects-view-archived" }]}
            value={view}
            onChange={setView}
          />
          <Segmented
            options={[{ key: "all", label: "All", testId: "projects-win-all" },
                      { key: "w4", label: "4 wks", testId: "projects-win-w4" },
                      { key: "w8", label: "8 wks", testId: "projects-win-w8" },
                      { key: "risk", label: <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-600" /> At risk</span>, testId: "projects-win-risk",
                        count: loading ? null : projects.filter((p) => riskReasons(p).length).length }]}
            value={window_}
            onChange={setWindow}
          />
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Group</span>
          <Segmented
            options={[{ key: "", label: "None", testId: "projects-group-none" },
                      { key: "foreman", label: "Foreman", testId: "projects-group-foreman" },
                      { key: "pm", label: "PM", testId: "projects-group-pm" }]}
            value={groupBy}
            onChange={setGroupBy}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={frsInputRef}
            type="file"
            accept=".xlsx,.xlsm,.xls"
            className="hidden"
            onChange={importFromFrs}
            data-testid="frs-import-input"
          />
          <Button
            variant="data"
            size="sm"
            disabled={importing}
            onClick={() => frsInputRef.current?.click()}
            data-testid="new-project-from-frs-btn"
            className="h-7 px-2 text-xs"
            title="Upload a master FRS — creates the job from the INFO tab and loads every filled-out scope"
          >
            <Upload className="mr-0.5 h-3.5 w-3.5" /> {importing ? "Importing…" : "FRS"}
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              data-testid="new-project-btn"
              className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              title="New Job"
              onClick={() => setForm({ ...EMPTY_FORM, job_number: nextJobNumber(projects) })}
            >
              <Plus className="mr-0.5 h-3.5 w-3.5" /> Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New Job</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="col-span-2">
                <Label>Job Name *</Label>
                <Input data-testid="project-name-input" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
              </div>
              <div>
                <Label>Client</Label>
                <select data-testid="project-client-select" className="h-9 w-full border border-gray-300 rounded-md px-2 text-sm bg-white" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">— none —</option>
                  {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}{c.rating ? ` · ${c.rating}` : ""}</option>))}
                </select>
              </div>
              <div><Label>Job #</Label><Input data-testid="project-job-input" value={form.job_number} onChange={(e) => setForm({ ...form, job_number: e.target.value })} /></div>
              <div className="col-span-2"><Label>Jobsite Address</Label><Input data-testid="project-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="1940 Hooker St." /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>ZIP</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
              <div>
                <Label>ABS Foreman</Label>
                <select data-testid="project-foreman-select" className="h-9 w-full border border-gray-300 rounded-md px-2 text-sm bg-white" value={form.foreman_id} onChange={(e) => setForm({ ...form, foreman_id: e.target.value })}>
                  <option value="">— none —</option>
                  {installers.map((i) => (<option key={i.id} value={i.id}>{i.name}{i.role ? ` · ${i.role}` : ""}</option>))}
                </select>
              </div>
              <div>
                <Label>ABS PM</Label>
                <select data-testid="project-pm-select" className="h-9 w-full border border-gray-300 rounded-md px-2 text-sm bg-white" value={form.pm_id} onChange={(e) => setForm({ ...form, pm_id: e.target.value })}>
                  <option value="">— none —</option>
                  {pms.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
                <p className="text-[10px] text-gray-400 mt-0.5">Manage PMs in Settings → PMs</p>
              </div>
              <div className="col-span-2 border-t border-gray-100 pt-2">
                <p className="text-xs font-semibold text-gray-700 mb-2">GC Contact (jobsite point-of-contact)</p>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Name</Label><Input data-testid="project-gc-name" value={form.gc_contact_name} onChange={(e) => setForm({ ...form, gc_contact_name: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input data-testid="project-gc-phone" value={form.gc_contact_phone} onChange={(e) => setForm({ ...form, gc_contact_phone: e.target.value })} placeholder="303-555-0123" /></div>
                  <div><Label>Email</Label><Input data-testid="project-gc-email" type="email" value={form.gc_contact_email} onChange={(e) => setForm({ ...form, gc_contact_email: e.target.value })} placeholder="gc@example.com" /></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="project-submit-btn" onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
        <span className="ml-auto text-xs text-slate-400" data-testid="ra-count">{loading ? "" : `${rows.length} job${rows.length === 1 ? "" : "s"}`}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[11px] text-muted-foreground">
            <tr>
              <th className="sticky top-0 z-10 w-8 bg-slate-50 px-2 py-2"></th>
              {[["job", "Job #", "px-2"], ["project", "Name", "px-3"], ["requests", "Requests", "px-3"], ["foreman", "Foreman", "px-3"], ["pm", "PM", "px-3"], ["area", "Next area", "px-3"], ["start", "Start", "px-3"],
                ["weeks", "Weeks out", "px-3"], ["completion", "Completion", "px-3"], ["material", "Material", "px-3"],
                ["doc", "Doc Ctrl", "px-3"], ["status", "Status", "px-3"], ["notes", "Notes", "px-2"]].map(([key, label, pad]) => (
                <th key={key} className={`sticky top-0 z-10 bg-slate-50 ${key === "material" || key === "doc" ? "text-center" : "text-left"} ${pad} py-2 font-semibold`}>
                  {key === "foreman" || key === "pm" ? (
                    <HeaderFilterMenu label={label} multi separator="|" sortOnLabel testid={`ra-${key}`}
                                      options={peopleOptions(key === "foreman" ? foremanName : (p) => p.pm_name)}
                                      value={key === "foreman" ? foremanF : pmF}
                                      onChange={(v) => (key === "foreman" ? setForemanF : setPmF)(v || "")}
                                      sortActive={sort?.key === key} direction={sort?.dir}
                                      onSort={(dir) => setSort({ key, dir })} />
                  ) : (
                  <button type="button" onClick={() => pressColumn(key)}
                          data-testid={`ra-sort-${key}`}
                          data-active={sort?.key === key ? sort.dir : undefined}
                          title="Press to sort; press again to reverse"
                          className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sort?.key === key ? "text-foreground" : ""}`}>
                    {label}
                    {(() => { const I = sort?.key !== key ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
                              return <I className={`h-3 w-3 ${sort?.key === key ? "" : "opacity-40"}`} />; })()}
                  </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={14} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={14} className="px-4 py-12 text-center text-gray-500" data-testid="ra-empty">
                <Briefcase className="w-6 h-6 mx-auto text-gray-400 mb-2" strokeWidth={1.5} />
                {query ? `No jobs match "${query}".` : window_ === "risk" ? "No job is at risk right now." : window_ !== "all" ? `No jobs start within ${window_ === "w4" ? 4 : 8} weeks.` : view === "archived" ? "No archived jobs." : "No jobs yet."}
              </td></tr>
            ) : items.map((x) => x.hdr !== undefined ? groupHeader(x) : ((p) => {
              const address = [p.address, p.city, p.state].filter(Boolean).join(", ");
              // a scope whose every room is complete has nothing left to plan — it drops off the list
              const scopeRows = (scopeDates[p.id] || (scopeSummary[p.id] || []).map((sc) => ({ scope: sc })))
                .filter((sc) => !sc.all_complete);
              const open = scopesOpen[p.id] === true;
              const dots = projectDots(p);
              const jobWeeks = weeksOut(jobStart(p));
              const jobChecks = rollupChecks(scopeRows);
              const nOpen = openRequests(p);
              return (
                <React.Fragment key={p.id}>
                <tr className="border-t border-gray-200 hover:bg-gray-50" data-testid={`project-row-${p.id}`}>
                  <td className="px-2 py-2.5 align-top">
                    {scopeRows.length > 0 && (
                      <button type="button" onClick={() => setScopesOpen((c) => ({ ...c, [p.id]: !open }))}
                              data-testid={`project-scopes-toggle-${p.id}`}
                              title={open ? "Hide scopes" : `Show ${scopeRows.length} scope${scopeRows.length === 1 ? "" : "s"}`}
                              className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-2.5 align-top">
                    <span className="text-xs font-mono font-semibold text-gray-700 tabular-nums" data-testid={`project-job-${p.id}`}>
                      {p.job_number || <span className="text-gray-400 font-sans font-normal">—</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Link to={`/projects/${p.id}`} data-testid={`project-link-${p.id}`} className="text-gray-900 hover:underline font-medium">
                        {p.project_name}
                      </Link>
                      <RiskBadge reasons={riskReasons(p)} testId={`project-risk-${p.id}`} label={p.project_name} />
                      {scopeRows.length > 0 && <span className="text-[10px] text-gray-400">{scopeRows.length} scope{scopeRows.length === 1 ? "" : "s"}</span>}
                      {String(p.archived_reason || "").startsWith("ops-") && (
                        <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"
                              title={`Archived automatically — Operations set the job ${p.archived_reason.slice(4)}`}>Auto</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                      {address && <span>{address}</span>}
                      {p.budgeted_hours > 0 && (
                        <span className="font-medium text-gray-500">{p.budgeted_hours.toLocaleString()} hrs</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {nOpen > 0 && (
                      <span className="inline-flex h-5 items-center rounded-full border border-amber-400 bg-amber-50 px-1.5 text-[10px] font-semibold text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
                            title={`${nOpen} open request${nOpen === 1 ? "" : "s"} across this job's scopes — expand to see them`}
                            data-testid={`project-requests-${p.id}`}>{nOpen} open</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap" data-testid={`project-foreman-${p.id}`}>
                    {shortName(foremanName(p)) || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap" data-testid={`project-pm-${p.id}`}>
                    {shortName(p.pm_name) || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5">
                    <ProjectStartCell project={p} scopeRows={scopeRows} actor={actor} onChanged={load} />
                  </td>
                  <td className={`px-3 py-2.5 text-[11px] tabular-nums whitespace-nowrap ${jobWeeks.tone}`} data-testid={`project-weeks-${p.id}`}>{jobWeeks.text || <span className="text-gray-400">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <ProjectCompletionCell project={p} scopeRows={scopeRows} />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <MaterialChecks colour={dots.material} testId={`project-material-${p.id}`} label={p.project_name}
                                    groups={liveScopes(p).filter((x) => x.material_detail).map((x) => ({ label: x.scope, colour: x.material, detail: x.material_detail }))} />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <DocChecks checks={jobChecks} testId={`project-doc-control-${p.id}`} label={p.project_name} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <OpsStatusPill value={p.ops_status} testId={`project-status-${p.id}`} />
                      {p.is_archived && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5" data-testid={`project-archived-${p.id}`}>
                          <Archive className="w-3 h-3" /> Archived
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2.5">
                    <NotesThread notes={notes[p.id] || []} title={p.project_name} testid={`ra-notes-${p.id}`}
                                 target={{ field_project_id: p.id }} onAdd={(t) => addNote(p, t)} />
                  </td>
                </tr>
                {open && (
                  <RaScopeRows project={p} scopeRows={scopeRows} actor={actor} onChanged={load} onLight={refreshLight}
                               onMob={(s) => setMobTarget({ project: p, sc: s })} treeOpen={treeOpen} setTreeOpen={setTreeOpen} />
                )}
                </React.Fragment>
              );
            })(x.p))}
          </tbody>
        </table>
      </div>

      <MobRequestDialog target={mobTarget} onClose={() => setMobTarget(null)} onChanged={refreshLight} />


      {/* Yes/No confirmation dialog for archive + activate */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="project-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              {confirm?.action === "archive" ? (<><AlertTriangle className="w-4 h-4 text-amber-500" /> Archive job?</>) : (<><RotateCcw className="w-4 h-4 text-emerald-600" /> Re-activate job?</>)}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 py-2">
            {confirm?.action === "archive" ? (
              <>You're about to archive <strong>{confirm?.project?.project_name}</strong>. It will be hidden from the active list, schedule pool, dispatch pool, and auto-dispatch. You can re-activate it any time.</>
            ) : (
              <>Re-activate <strong>{confirm?.project?.project_name}</strong>? It'll show up again in active lists and dispatch pools.</>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="project-confirm-no">No</Button>
            <Button
              onClick={() => confirm?.action === "archive" ? doArchive(confirm.project) : doActivate(confirm.project)}
              className={confirm?.action === "archive" ? "bg-gray-700 hover:bg-gray-800 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="project-confirm-yes"
            >
              Yes, {confirm?.action === "archive" ? "archive" : "activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
